import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CHECKOUT_RULE_DOCUMENT_TYPES,
  CheckoutRuleDocumentType,
} from '../../../interfaces'
import { ICheckoutRuleValue } from '../../../graphQL/queries/getCheckoutRule/types'
import { IRule } from '../../../graphQL/queries/checkoutSessionRule/types'
import type { RootState } from '../../store'
import {
  CheckoutAvailablePayload,
  CheckoutClearSelectionPayload,
  CheckoutLoadingPayload,
  CheckoutSelectionPayload,
  ICheckoutAvailable,
  ICheckoutRuleUiFlags,
  ICheckoutState,
} from './types'

/**
 * The three derived flags for one rule type, from the two facts that move
 * them.
 *
 * Pure and total: it answers for a rule type nothing has fetched, which is
 * what lets `initialState` seed every key before a screen exists.
 */
const deriveUiFlags = (
  available: ICheckoutAvailable | undefined,
  selection: ICheckoutRuleValue | undefined,
): ICheckoutRuleUiFlags => ({
  /*
   * `loaded` is the load-bearing half. Testing only `data.length === 0` would
   * hide nothing before the fetch lands, because an absent entry has no
   * length — and the shopper would read "no options" as the answer.
   */
  emptyHidden: !(
    available?.loadingState === 'loaded' && available.data.length === 0
  ),
  errorHidden: available?.loadingState !== 'error',
  /*
   * Keyed on `data`, not on the key's presence. `setCheckoutRuleSession` sends
   * `value` as a JSON leaf and nothing server-side rejects an empty `data`, so
   * a selection carrying one would enable the CTA and write a choice that is
   * not a choice.
   */
  confirmDisabled: !selection?.data,
})

/**
 * Rewrites one rule type's flags from the state around them.
 *
 * Called by every reducer that can move one. Recomputing beats patching: the
 * three flags read four facts between them, and a reducer that patched only
 * the flag it "obviously" affects would drift the first time a reducer touched
 * two of those facts at once.
 */
const recomputeUi = (
  state: ICheckoutState,
  ruleType: CheckoutRuleDocumentType,
) => {
  state.ui[ruleType] = deriveUiFlags(
    state.available[ruleType],
    state.selections[ruleType],
  )
}

/**
 * One all-quiet entry per rule type.
 *
 * The exhaustiveness guard lives on `CHECKOUT_RULE_DOCUMENT_TYPES` itself, so
 * a new union member fails the build there rather than silently seeding one
 * key fewer here. The cast is what that guard buys: an empty object is not yet
 * the full record, and there is no way to say so mid-construction.
 */
const seedUiFlags = (): ICheckoutState['ui'] => {
  const seeded = {} as ICheckoutState['ui']
  CHECKOUT_RULE_DOCUMENT_TYPES.forEach((ruleType) => {
    seeded[ruleType] = deriveUiFlags(undefined, undefined)
  })
  return seeded
}

export const initialState: ICheckoutState = {
  selections: {},
  available: {},
  ui: seedUiFlags(),
  lastAppliedRules: undefined,
}

export const checkoutSlice = createSlice({
  name: 'checkoutSlice',
  initialState,
  reducers: {
    /**
     * Records the shopper's pick for one rule type.
     *
     * Overwrites rather than merging, which mirrors the server:
     * `Session.setCheckoutRuleById` filters out the existing entry for the
     * type and appends, so a second pick replaces the first there too. A merge
     * here would let a stale `amount` survive a change of `data`.
     *
     * Writing this does **not** persist anything. The mutation is the writer;
     * this is the optimistic copy the picker renders from while it is in
     * flight, and it is discarded on reload.
     */
    setSelection: (
      state,
      { payload }: PayloadAction<CheckoutSelectionPayload>,
    ) => {
      state.selections[payload.ruleType] = payload.value
      recomputeUi(state, payload.ruleType)
    },
    /**
     * Forgets the shopper's pick for one rule type.
     *
     * The rollback half of an optimistic write, and the reason it exists:
     * `setSelection` overwrites, so it can restore a previous pick but cannot
     * express "there was no pick". A failed `setCheckoutRuleSession` on a
     * shopper's first choice needs exactly that.
     *
     * Deleting the key rather than writing a blank `ICheckoutRuleValue` keeps
     * `selectSelectionByRuleType` honest — its `undefined` means "nothing
     * chosen", and a `{ data: '', amount: 0 }` sentinel would have to be
     * unpicked at every read.
     *
     * Leaves `available` alone. What the storefront offers did not change
     * because a write failed.
     */
    clearSelection: (
      state,
      { payload }: PayloadAction<CheckoutClearSelectionPayload>,
    ) => {
      delete state.selections[payload.ruleType]
      recomputeUi(state, payload.ruleType)
    },
    /**
     * Stores a completed fetch and marks it `loaded`.
     *
     * `loaded` is set here rather than left to a separate `setLoadingState`
     * call, so no render can observe options that have arrived under a
     * `loading` flag. An empty `data` is a legitimate result and still reads
     * `loaded` — that is the pilot's answer for `PAYMENT_METHODS` today.
     */
    setAvailable: (
      state,
      { payload }: PayloadAction<CheckoutAvailablePayload>,
    ) => {
      state.available[payload.ruleType] = {
        data: payload.data,
        loadingState: 'loaded',
      }
      recomputeUi(state, payload.ruleType)
    },
    /**
     * Moves one rule type's fetch between states without touching its data.
     *
     * Keeps whatever was already loaded, so a failed refetch shows the
     * previous options beside its error rather than blanking the screen. The
     * `orders` slice takes the same posture on a failed load-more.
     *
     * `error` is cleared unless this call carries one, so recovering from a
     * failure leaves nothing stale behind.
     */
    setLoadingState: (
      state,
      { payload }: PayloadAction<CheckoutLoadingPayload>,
    ) => {
      const existing: ICheckoutAvailable = state.available[payload.ruleType] ?? {
        data: [],
        loadingState: 'idle',
      }

      state.available[payload.ruleType] = {
        ...existing,
        loadingState: payload.loadingState,
        error: payload.error,
      }
      recomputeUi(state, payload.ruleType)
    },
    /**
     * Stores the rules from the last `checkoutSessionRule` response.
     *
     * COM-950's pre-flight writes this and its redirect reads it. Nothing in
     * COM-947 does either.
     */
    setLastAppliedRules: (state, { payload }: PayloadAction<IRule[]>) => {
      state.lastAppliedRules = payload
    },
    /**
     * Drops the shopper's picks, keeping what the storefront offers.
     *
     * The asymmetry is the point. Selections belong to a session and mean
     * nothing in the next one. The offered options belong to the storefront
     * and survive — re-fetching them on a session change would be a request
     * for an answer already held.
     *
     * ⚠️ **Nothing dispatches this.** No slice in this repo reacts to a
     * session change, and `defaultSlice.sessionId` is written once from the
     * URL, so the only transition production performs is `undefined` → real
     * id. It exists for the screen that first needs it to call, not to be
     * wired to a watcher here.
     */
    resetSelectionsForNewSession: (state) => {
      state.selections = {}
      state.lastAppliedRules = undefined
      CHECKOUT_RULE_DOCUMENT_TYPES.forEach((ruleType) => {
        recomputeUi(state, ruleType)
      })
    },
  },
})

export const {
  setSelection,
  clearSelection,
  setAvailable,
  setLoadingState,
  setLastAppliedRules,
  resetSelectionsForNewSession,
} = checkoutSlice.actions

/**
 * The shopper's pick for one rule type, or `undefined`.
 *
 * Curried so a component names the rule type at module scope and passes the
 * result straight to `useAppSelector`, rather than building a new closure on
 * every render.
 *
 * `RootState` is imported as a **type**. `store.tsx` imports this module, so a
 * value import would close a runtime cycle; a type-only import is erased.
 */
export const selectSelectionByRuleType =
  (ruleType: CheckoutRuleDocumentType) =>
  (state: RootState): ICheckoutRuleValue | undefined =>
    state.checkoutSlice.selections[ruleType]

/**
 * What the storefront offers for one rule type, or `undefined` when nothing
 * has asked.
 *
 * ⚠️ `undefined` and `{ data: [], loadingState: 'loaded' }` are different
 * answers. The first means no fetch has run; the second means the storefront
 * offers nothing. A caller that reads `?.data ?? []` erases the distinction
 * and renders an empty-state message over a screen that has not loaded.
 */
export const selectAvailableByRuleType =
  (ruleType: CheckoutRuleDocumentType) =>
  (state: RootState): ICheckoutAvailable | undefined =>
    state.checkoutSlice.available[ruleType]

export const selectLastAppliedRules = (
  state: RootState,
): IRule[] | undefined => state.checkoutSlice.lastAppliedRules

export default checkoutSlice.reducer
